import { MenuButton, MenuItemWithIcon, ModalOpenButton } from "@msys/ui";
import {
  DataObject as DataObjectIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { CircularProgress, Divider, useTheme } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useFeature } from "../../../../common/FeatureFlags.js";
import { useUserData } from "../../../auth/useUserData.js";
import { PdfFileIcon } from "../../../commons/icons/PdfFileIcon.js";
import { GenerateQuotePdfModal } from "../../quote-documents/GenerateQuotePdfModal.js";
import { QuoteDocumentsMenuItems } from "../../quote-documents/QuoteDocumentsMenuItems.js";
import { UploadQuoteDocumentModal } from "../../quote-documents/UploadQuoteDocumentModal.js";
import { useDownloadQuotePdfOptions } from "../hooks/useDownloadQuotePdfOptions.js";
import {
  QuoteDataCalculationType,
  useGetQuoteData,
} from "../hooks/useGetQuoteData.js";
import { QuoteDataInspectorModal } from "../QuoteDataInspectorModal.js";
import { CreateQuotePdfIconButton_QuoteFragment } from "./CreateQuotePdfIconButton.generated.js";

export const CreateQuotePdfIconButton = ({
  docId,
  projectId,
  doc,
}: {
  docId: string;
  projectId: string;
  doc: CreateQuotePdfIconButton_QuoteFragment;
}) => {
  const theme = useTheme();
  const { t } = useTranslate(["Quote", "Global"]);
  const { currentUser } = useUserData();
  const debug = useFeature("DebugOutput");

  // Producing a document is the owning organisation's operation — a contractee
  // on a share only reads what the owner published, so it reaches the saved
  // documents below but neither generation nor upload.
  const isOwner =
    currentUser?.organisation.id === doc.owningSystemOrganisationId;
  const canManage =
    isOwner && doc.viewerPermissions.includes("quote_document_update");

  return (
    <MenuButton
      Icon={<PdfFileIcon />}
      tooltip={t("Download PDF", { ns: "Quote" })}
      menuProps={{
        slotProps: {
          paper: {
            style: {
              width: theme.layout.filters.popoverWidth.lg,
              minWidth: theme.layout.filters.popoverWidth.lg,
              maxWidth: theme.layout.filters.popoverWidth.lg,
            },
          },
        },
      }}
    >
      {isOwner && (
        <ModalOpenButton
          Modal={GenerateQuotePdfModal}
          modalProps={{ projectId, docId, doc, canSave: canManage }}
        >
          <MenuItemWithIcon
            icon={<PdfFileIcon />}
            sx={{ whiteSpace: "normal" }}
          >
            {t("Generate", { ns: "Global" })}
          </MenuItemWithIcon>
        </ModalOpenButton>
      )}
      {canManage && (
        <ModalOpenButton
          Modal={UploadQuoteDocumentModal}
          modalProps={{ projectId, quoteId: docId }}
        >
          <MenuItemWithIcon
            icon={<UploadFileIcon />}
            sx={{ whiteSpace: "normal" }}
          >
            {t("Upload", { ns: "Global" })}
          </MenuItemWithIcon>
        </ModalOpenButton>
      )}

      <QuoteDocumentsMenuItems quote={doc} showDivider={isOwner} />

      {debug && (
        <InspectQuoteDataMenuItems
          docId={docId}
          projectId={projectId}
          doc={doc}
        />
      )}
    </MenuButton>
  );
};

/**
 * Behind the `DebugOutput` flag, and its own component because
 * `useDownloadQuotePdfOptions` queries the organisation's PDF templates on
 * mount — mounting it only for a debug user keeps that request off every quote
 * view.
 */
function InspectQuoteDataMenuItems({
  docId,
  projectId,
  doc,
}: {
  docId: string;
  projectId: string;
  doc: CreateQuotePdfIconButton_QuoteFragment;
}) {
  const { t } = useTranslate(["Quote"]);
  const { handleFetch: fetchQuoteData, loadingQuoteData } = useGetQuoteData({
    projectId,
    docId,
  });
  const [inspectorData, setInspectorData] = React.useState<unknown>(null);
  const { hasPdfGeneratorIntegration, hasSelectedCalculations } =
    useDownloadQuotePdfOptions({ doc });

  const getHandleInspect =
    (calculationType: QuoteDataCalculationType) =>
    async (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();
      const quoteData = await fetchQuoteData(calculationType);
      if (quoteData !== null && quoteData !== undefined) {
        setInspectorData(quoteData);
      }
    };

  if (!hasPdfGeneratorIntegration) return null;

  return (
    <>
      <Divider />
      {hasSelectedCalculations && (
        <MenuItemWithIcon
          icon={
            loadingQuoteData === "inspect-agreed" ? (
              <CircularProgress size="1.5rem" color={"primary"} />
            ) : (
              <DataObjectIcon />
            )
          }
          onClick={getHandleInspect("agreed")}
          disabled={loadingQuoteData === "inspect-agreed"}
          sx={{ whiteSpace: "normal" }}
        >
          {`${t("Inspect quote data", { ns: "Quote" })} (${t("agreed", { ns: "Quote" })})`}
        </MenuItemWithIcon>
      )}
      <MenuItemWithIcon
        icon={
          loadingQuoteData === "inspect-proposed" ? (
            <CircularProgress size={24} color={"primary"} />
          ) : (
            <DataObjectIcon />
          )
        }
        onClick={getHandleInspect("proposed")}
        disabled={loadingQuoteData === "inspect-proposed"}
        sx={{ whiteSpace: "normal" }}
      >
        {hasSelectedCalculations
          ? `${t("Inspect quote data", { ns: "Quote" })} (${t("proposed", { ns: "Quote" })})`
          : t("Inspect quote data", { ns: "Quote" })}
      </MenuItemWithIcon>
      {inspectorData !== null && (
        <QuoteDataInspectorModal
          quoteData={inspectorData}
          handleClose={() => setInspectorData(null)}
        />
      )}
    </>
  );
}
